/**
 * BuildASpecModal.tsx — CC left-nav "Build-A-Spec" interactive spec validator.
 *
 * track:build-a-spec-cc-nav-modal (backlog spec seq #41984). Floating,
 * draggable, resizable modal for drafting a spec and validating it against
 * the same file-reference/blast-level checks Phase 1a already runs, before
 * filing it to backlog. Also exposed as the `validate_spec` MCP tool
 * (server/mcp/teamLogStateless.ts) via the same shared logic
 * (src/lib/specValidator.ts).
 *
 * Branding: lives under src/components/ControlCenter/, so it follows CC v2's
 * own chrome pattern (index.tsx) — CSS-var tokens for border/shadow, plus
 * the same hardcoded #7BCBBA/#054750 header pair CC v2 uses instead of
 * var(--teal-dark) (that token has drifted from the real chat-modal value —
 * #1A6B6B vs #054750 — see FloatingNotesTasks.tsx's header comment). CC v2's
 * own panel chrome is itself documented as the "FloatingChat brand-standard
 * rebrand" (index.tsx's border-weight-convention comment), so this matches
 * the chat modal by following that rebrand rather than FloatingChat's older
 * un-themed hardcoded-hex version directly. Drag/resize (8-direction, edges
 * + corners per spec) follows FloatingChat/FloatingNotesTasks' hand-rolled
 * mousedown/mousemove pattern — no library exists in this repo.
 */
import { useEscapeToClose } from "../../hooks/useEscapeToClose";
import { useCallback, useEffect, useRef, useState } from "react";
import { ResizeHandle } from "../ResizeHandle";
import { PencilIcon } from "./icons";
import { validateSpec, type SpecValidationResult } from "../../lib/specValidator";
import { useKnownRepos } from "./useKnownRepos";
import { agentIdFromUser, getAuthHeader, onUserChange, viewerAuthor } from "../../lib/auth";
import { type ProjectRecord } from "../../lib/projectsMigration";
import { ProjectSelector } from "../ProjectSelector";
import type { Feature } from "../../types";

const DEFAULT_SIZE = { w: 640, h: 520 };
const MIN_SIZE = { w: 480, h: 400 };
const POS_KEY = "relay.buildASpec.pos";
const SIZE_KEY = "relay.buildASpec.size";

// Every Team Log write from this modal is authored as the signed-in member
// (viewerAuthor() — the same email-local-part id the server's
// assertAuthorMatchesCaller derives), never a fixed name.
function currentViewerAuthor(): string | null {
  try {
    return viewerAuthor();
  } catch {
    return null;
  }
}

export interface BuildASpecModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  projects: ProjectRecord[];
}

function loadPos(): { x: number; y: number } | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(POS_KEY) || "null");
    if (v && typeof v.x === "number" && typeof v.y === "number") return v;
  } catch {}
  return null;
}

function loadSize(): { w: number; h: number } {
  try {
    const v = JSON.parse(sessionStorage.getItem(SIZE_KEY) || "null");
    if (v && typeof v.w === "number" && typeof v.h === "number") return v;
  } catch {}
  return DEFAULT_SIZE;
}

export function BuildASpecModal({ open, onOpenChange, workspaceId, projects }: BuildASpecModalProps) {
  const [authorId, setAuthorId] = useState<string | null>(currentViewerAuthor);
  useEffect(() => onUserChange(user => setAuthorId(agentIdFromUser(user))), []);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(loadPos);
  const [size, setSize] = useState(loadSize);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [result, setResult] = useState<SpecValidationResult | null>(null);
  const [copied, setCopied] = useState(false);
  // Fetches once on first open, cached for the rest of the session (spec's
  // requirement) — track:build-a-spec-cross-repo-detection (seq #42013).
  const knownRepos = useKnownRepos(open);

  // ─── track:feature-intelligence-phase2 (Team Log seq #42191 Parts 1-4) ───
  // "Validate" now also posts the spec to the Team Log (tagged with its
  // recommended_tag) once it passes, since validatedSpecSeq — the real
  // sequence number save_spec_to_backlog needs — can only come from an
  // actual posted message, not from local validation alone. See the PR
  // description for why this is a necessary Part 1/2 prerequisite rather
  // than scope creep: save_spec_to_backlog rejects any specSeq that isn't
  // an existing message tagged spec-validated/spec-validated:warned.
  const [validatedSpecSeq, setValidatedSpecSeq] = useState<number | null>(null);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [seqCopied, setSeqCopied] = useState(false);

  // Part 2 — "Save to Backlog." No persistence needed across sessions (spec's
  // requirement) — plain in-memory state, reset on edit/close like everything
  // else here.
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [savedBacklogSeq, setSavedBacklogSeq] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Part 3 — feature picker. No project picker exists anywhere in this app
  // yet (App.tsx has no single "current project" concept — see PR
  // description); a minimal project select is added to the inline
  // save-form itself, scoped tightly to "what save_spec_to_backlog needs,"
  // not the fuller project-picker described as a separate later phase in
  // the master spec (seq #42191 §8's own Phase 2 bundles them — this
  // build's literal Part list does not).
  //
  // track:key-outcome-coverage-check (seq #43386) — moved to the top level
  // (rendered before Validate, not only inside the save-form) so the
  // KEY_OUTCOME_COVERAGE check has a project to check against at
  // Validate-click time. The save-form still uses the same state — no
  // second picker, no second fetch.
  const [selectedProjectSlug, setSelectedProjectSlug] = useState("");
  const [features, setFeatures] = useState<Feature[] | null>(null);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresError, setFeaturesError] = useState(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState("");
  const [trackType, setTrackType] = useState<"build" | "investigation">("build");

  const posRef = useRef(pos);
  posRef.current = pos;
  const sizeRef = useRef(size);
  sizeRef.current = size;

  // "remembers last size for session" (spec) — sessionStorage, not
  // localStorage, so it resets on a fresh browser session.
  useEffect(() => {
    try {
      sessionStorage.setItem(SIZE_KEY, JSON.stringify(size));
    } catch {}
  }, [size]);
  useEffect(() => {
    if (!pos) return;
    try {
      sessionStorage.setItem(POS_KEY, JSON.stringify(pos));
    } catch {}
  }, [pos]);

  const defaultPos = useCallback(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return { x: Math.max(16, Math.round((vw - sizeRef.current.w) / 2)), y: Math.max(16, Math.round((vh - sizeRef.current.h) / 2)) };
  }, []);

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!posRef.current) setPos(defaultPos());
      const startX = e.clientX;
      const startY = e.clientY;
      const origin = posRef.current || defaultPos();
      const onMove = (ev: MouseEvent) => {
        const nx = Math.min(Math.max(8, origin.x + (ev.clientX - startX)), window.innerWidth - 80);
        const ny = Math.min(Math.max(8, origin.y + (ev.clientY - startY)), window.innerHeight - 60);
        setPos({ x: nx, y: ny });
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [defaultPos]
  );

  const startResize = useCallback(
    (e: React.MouseEvent, dir: string) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const sw = sizeRef.current.w;
      const sh = sizeRef.current.h;
      const origin = posRef.current || defaultPos();
      const ox = origin.x;
      const oy = origin.y;
      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let nw = sw;
        let nh = sh;
        let nx = ox;
        let ny = oy;
        if (dir.includes("e")) nw = Math.max(MIN_SIZE.w, sw + dx);
        if (dir.includes("w")) { nw = Math.max(MIN_SIZE.w, sw - dx); nx = ox + (sw - nw); }
        if (dir.includes("s")) nh = Math.max(MIN_SIZE.h, sh + dy);
        if (dir.includes("n")) { nh = Math.max(MIN_SIZE.h, sh - dy); ny = oy + (sh - nh); }
        setSize({ w: nw, h: nh });
        setPos({ x: nx, y: ny });
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [defaultPos]
  );

  // Editing after a spec was posted/saved invalidates that state — the seq
  // number and any backlog save belonged to the previous content, not
  // whatever the human is now typing.
  const invalidatePostedState = useCallback(() => {
    setResult(null);
    setValidatedSpecSeq(null);
    setPostError(null);
    setShowSaveForm(false);
    setSavedBacklogSeq(null);
    setSaveError(null);
  }, []);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(e.target.value);
      invalidatePostedState();
    },
    [invalidatePostedState]
  );
  const handleBodyChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setBody(e.target.value);
      invalidatePostedState();
    },
    [invalidatePostedState]
  );

  const runValidate = useCallback(async () => {
    // track:key-outcome-coverage-check (seq #43386) — undefined when no
    // project is selected, or when the selected project has no features
    // yet (still loading, fetch failed, or genuinely empty) — validateSpec
    // treats undefined identically to "skip this check," matching the
    // conditional-not-required requirement. features.length === 0 must map
    // to undefined, not [], so it doesn't accidentally warn on an
    // intentionally-empty keyOutcomes union.
    const projectKeyOutcomes =
      features && features.length > 0 ? features.flatMap(f => f.keyOutcomes) : undefined;
    const validation = validateSpec(
      title,
      body,
      knownRepos ?? undefined,
      projectKeyOutcomes && projectKeyOutcomes.length > 0 ? projectKeyOutcomes : undefined,
      undefined,
      trackType,
    );
    setResult(validation);
    // Already posted this exact validated content — a re-click of Validate
    // (no edits since) must not create a second immutable Team Log post.
    if (!validation.passed || validatedSpecSeq !== null) return;

    setPosting(true);
    setPostError(null);
    try {
      const headers = { ...(await getAuthHeader()), "Content-Type": "application/json" };
      // track:key-outcome-coverage-check (seq #43386) — passed=true now
      // yields "spec-validated" or "spec-validated:warned" (soft warning
      // present) — see specValidator.ts. Posting proceeds either way; a
      // soft warning is advisory, never blocking.
      const tag = validation.recommended_tag ?? "spec-validated";
      // track:feature-lifecycle-join-keys (seq #45053/#45072) — the spec
      // message itself now carries projectId/featureId, not just the
      // backlog item saveSpecToBacklog derives from it later. Same
      // optional-param shape confirmSaveToBacklog already sends below —
      // featureId omitted (not empty string) when nothing is selected, so
      // postMessage's `input.featureId` stays genuinely absent rather than
      // a stored empty string.
      const res = await fetch("/api/messages", {
        method: "POST",
        headers,
        body: JSON.stringify({
          author: authorId,
          content: `## ${title.trim() || "Untitled spec"}\n\n${body}`,
          tags: [tag, "spec-draft"],
          workspace_id: workspaceId,
          projectId: selectedProjectSlug || undefined,
          featureId: selectedFeatureId || undefined,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || errBody.error || `Post failed (HTTP ${res.status})`);
      }
      const posted = await res.json();
      setValidatedSpecSeq(posted.sequence);
    } catch (err: any) {
      setPostError(err?.message || "Failed to post spec to Team Log.");
    } finally {
      setPosting(false);
    }
  }, [title, body, knownRepos, workspaceId, validatedSpecSeq, features, selectedProjectSlug, selectedFeatureId, trackType]);

  // Part 3 — fetched whenever the top-level project select changes, not
  // once on modal open: no project is known until the human picks one (see
  // the project-picker note above), so "once on open" can't apply here.
  // track:key-outcome-coverage-check (seq #43386) — no longer gated on
  // showSaveForm: the same fetch now also feeds runValidate's
  // KEY_OUTCOME_COVERAGE check, which needs features available before the
  // save-form ever opens.
  useEffect(() => {
    if (!selectedProjectSlug) {
      setFeatures(null);
      setFeaturesError(false);
      return;
    }
    let cancelled = false;
    setFeaturesLoading(true);
    setFeaturesError(false);
    (async () => {
      try {
        const headers = await getAuthHeader();
        const res = await fetch(`/api/workspace/project-background/${encodeURIComponent(selectedProjectSlug)}`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const bg = data?.background;
        setFeatures(Array.isArray(bg?.features) ? bg.features : []);
      } catch {
        if (cancelled) return;
        // Spec: "on error, hide the picker and proceed without it."
        setFeatures(null);
        setFeaturesError(true);
      } finally {
        if (!cancelled) setFeaturesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectSlug]);

  // track:key-outcome-coverage-check (seq #43386) — selectedProjectSlug is
  // now chosen at the top level, before Validate; opening the save-form no
  // longer resets it (the human already picked a project, if any). Only
  // the feature selection (specific to the save step) resets.
  const openSaveForm = useCallback(() => {
    setSelectedFeatureId("");
    setSaveError(null);
    setShowSaveForm(true);
  }, []);

  const cancelSaveForm = useCallback(() => {
    setShowSaveForm(false);
    setSaveError(null);
  }, []);

  const confirmSaveToBacklog = useCallback(async () => {
    if (validatedSpecSeq === null || !selectedProjectSlug) return;
    setSaving(true);
    setSaveError(null);
    try {
      const headers = { ...(await getAuthHeader()), "Content-Type": "application/json" };
      const res = await fetch("/api/workspace/save-spec-to-backlog", {
        method: "POST",
        headers,
        body: JSON.stringify({
          specSeq: validatedSpecSeq,
          projectId: selectedProjectSlug,
          featureId: selectedFeatureId || undefined,
          trackType,
          author: authorId,
          workspace_id: workspaceId,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || errBody.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      setSavedBacklogSeq(data.backlogItemSeq);
      setShowSaveForm(false);
    } catch (err: any) {
      setSaveError(err?.message || "Failed to save to backlog.");
    } finally {
      setSaving(false);
    }
  }, [validatedSpecSeq, selectedProjectSlug, selectedFeatureId, trackType, workspaceId]);

  const copySeq = useCallback(() => {
    if (validatedSpecSeq === null) return;
    navigator.clipboard
      .writeText(`#${validatedSpecSeq}`)
      .then(() => {
        setSeqCopied(true);
        setTimeout(() => setSeqCopied(false), 1500);
      })
      .catch(() => {});
  }, [validatedSpecSeq]);

  const copyToBacklog = useCallback(() => {
    // track:build-a-spec-mandatory-validation (seq #42077) — a plain
    // clipboard copy can't attach a real tag to whatever composer this
    // gets pasted into, but naming the recommended one here means a human
    // doesn't have to re-derive it by hand when the workspace requires it.
    const tagHint = result?.recommended_tag ? `\n\ntags: ${result.recommended_tag}` : "";
    const formatted = `## ${title.trim() || "Untitled spec"}\n\n${body}${tagHint}`;
    navigator.clipboard
      .writeText(formatted)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [title, body, result]);

  // seq #42029 — spec content/results must reset on close so the next open
  // starts blank; remembered size/pos (loadPos/loadSize, sessionStorage)
  // are deliberately untouched.
  const handleClose = useCallback(() => {
    setTitle("");
    setBody("");
    setResult(null);
    setCopied(false);
    invalidatePostedState();
    setSelectedProjectSlug("");
    setSelectedFeatureId("");
    setFeatures(null);
    setFeaturesError(false);
    setSeqCopied(false);
    onOpenChange(false);
  }, [onOpenChange, invalidatePostedState]);

  useEscapeToClose(open, handleClose);

  if (!open) return null;

  // track:key-outcome-coverage-check (seq #43386) — third visual state:
  // clean pass (no warnings at all) / soft-warn (passed, but a soft
  // warning like KEY_OUTCOME_COVERAGE is present) / hard-fail (not
  // passed). Only the soft-warn case is new; clean-pass and hard-fail
  // behave exactly as before.
  const softWarnings = result?.warnings.filter(w => w.severity === "soft") ?? [];
  const hasSoftWarnings = result?.passed === true && softWarnings.length > 0;

  const p = pos || defaultPos();
  const RESIZE_DIRS: Array<{ dir: string; style: React.CSSProperties }> = [
    { dir: "n", style: { top: -3, left: 8, right: 8, height: 6, cursor: "ns-resize" } },
    { dir: "s", style: { bottom: -3, left: 8, right: 8, height: 6, cursor: "ns-resize" } },
    { dir: "w", style: { left: -3, top: 8, bottom: 8, width: 6, cursor: "ew-resize" } },
    { dir: "e", style: { right: -3, top: 8, bottom: 8, width: 6, cursor: "ew-resize" } },
    { dir: "nw", style: { top: -4, left: -4, width: 12, height: 12, cursor: "nwse-resize" } },
    { dir: "ne", style: { top: -4, right: -4, width: 12, height: 12, cursor: "nesw-resize" } },
    { dir: "sw", style: { bottom: -4, left: -4, width: 12, height: 12, cursor: "nesw-resize" } },
    { dir: "se", style: { bottom: -4, right: -4, width: 12, height: 12, cursor: "nwse-resize" } },
  ];

  return (
    <div
      role="dialog"
      aria-label="Build-A-Spec"
      className="fixed z-[9000] flex flex-col overflow-hidden rounded-[16px] border-2 border-[var(--teal,#5FC4C0)] bg-[var(--paper,#fff)] text-[var(--ink)] shadow-[0_24px_60px_rgba(5,71,80,0.22),0_4px_12px_rgba(20,20,18,0.08)]"
      style={{ left: p.x, top: p.y, width: size.w, height: size.h, fontFamily: "var(--font-ui)" }}
    >
      {/* Misregistration ghosts — ControlCenter/index.tsx's own port of
          FloatingChat's brand-standard off-register print effect. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-0.5 -z-10 rounded-[16px] border-2 border-[var(--orange)] opacity-[0.55]"
        style={{ transform: "translate(4px, 5px)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-0.5 -z-10 rounded-[16px] border-2 border-[var(--yellow)] opacity-[0.45]"
        style={{ transform: "translate(-3px, 2px)" }}
      />

      <div
        className="flex cursor-move items-center gap-2.5 border-b border-[var(--border)] bg-[#7BCBBA] px-3 py-2.5"
        onMouseDown={startDrag}
      >
        <PencilIcon size={18} style={{ color: "#054750", flexShrink: 0 }} />
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="text-[16px] font-semibold leading-tight text-[#054750]">Build-A-Spec</span>
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[rgba(5,71,80,0.75)]">
            Spec Validator
          </span>
        </div>
        <button
          type="button"
          onClick={handleClose}
          onMouseDown={e => e.stopPropagation()}
          aria-label="Close"
          title="Close"
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-none bg-[rgba(5,71,80,0.12)] text-2xl font-bold leading-none text-[#054750] transition-colors hover:bg-[rgba(5,71,80,0.22)]"
        >
          ×
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 overflow-auto p-3.5">
        {/* track:skills-retirement-relay (decision #49035, spec #49036 Slice 2) —
            relocated from the old Skills system's "Spec" skill content
            (server/api/skills.ts SPEC_CONTENT), which used to inject this only
            into spawned dev agents. Static in-app help now, for anyone using
            this modal, human or agent. */}
        <details className="rounded-[8px] border border-[var(--line)] px-2.5 py-1.5 text-[12px] text-[var(--ink-soft)]">
          <summary className="cursor-pointer select-none font-semibold text-[var(--ink)]">
            Spec-writing tips
          </summary>
          <div className="mt-2 flex flex-col gap-1.5 leading-normal">
            <p>
              Before writing: check for an approved PRD (tagged prd-draft in the Team Log) and
              write the spec against what it says users need, not against what's technically
              convenient. If no PRD exists, note that in the spec and proceed anyway.
            </p>
            <p>
              Include: problem statement, proposed solution, scope boundaries (what's explicitly
              out of scope), acceptance criteria as verifiable statements, and any data model
              changes.
            </p>
            <p>
              Validate before filing — a passing Validate run above earns the spec-validated tag.
              A validation result carrying a warning is worth a second look before treating the
              spec as final.
            </p>
          </div>
        </details>
        <input
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Short title for this spec…"
          className="rounded-[8px] border border-[var(--line)] px-2.5 py-1.5 text-[13px] text-[var(--ink)] outline-none"
        />
        <textarea
          value={body}
          onChange={handleBodyChange}
          placeholder="Describe the work — what changes, which files, which repos, which Firestore collections…"
          className="min-h-[120px] flex-1 resize-none rounded-[8px] border border-[var(--line)] px-2.5 py-2 text-[13px] leading-normal text-[var(--ink)] outline-none"
        />
        {/* track:key-outcome-coverage-check (seq #43386) — top-level, so a
            project is selectable before Validate, not only in the
            save-form (Phase 2's original scope). Reused by both
            runValidate's KEY_OUTCOME_COVERAGE check and the feature picker
            just below — one selection, one fetch. */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-soft)" }}>
          Project (optional)
          <div className="mt-0.5">
            <ProjectSelector
              mode="optional"
              projects={projects}
              noneLabel="No project"
              value={selectedProjectSlug || null}
              onChange={slug => {
                setSelectedProjectSlug(slug ?? "");
                setSelectedFeatureId("");
              }}
            />
          </div>
        </div>

        {/* track:feature-lifecycle-join-keys (seq #45053/#45072) — moved to
            the top level, same move the project picker itself already made
            in track:key-outcome-coverage-check, and for the same reason:
            runValidate posts the spec message on the very first successful
            Validate click, before the save-form (and this picker, formerly
            nested inside it) ever renders — a featureId selected only after
            that post can never reach it. Selecting a feature here before
            Validate is what makes "tied from the start of authoring" real
            instead of a no-op. */}
        {selectedProjectSlug && featuresLoading && (
          <div style={{ fontSize: 11, color: "var(--ink-soft)" }}>Loading features…</div>
        )}
        {selectedProjectSlug && !featuresLoading && !featuresError && features && features.length > 0 && (
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-soft)" }}>
            Feature (optional)
            <select
              value={selectedFeatureId}
              onChange={e => setSelectedFeatureId(e.target.value)}
              className="mt-0.5 block w-full rounded-[6px] border border-[var(--line)] px-1.5 py-1 text-[12px]"
            >
              <option value="">None</option>
              {features.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name} ({f.status})
                </option>
              ))}
            </select>
          </label>
        )}

        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-soft)" }}>
          Track type (applied on Save to Backlog)
          <select
            value={trackType}
            onChange={e => setTrackType(e.target.value === "investigation" ? "investigation" : "build")}
            className="mt-0.5 block w-full rounded-[6px] border border-[var(--line)] px-1.5 py-1 text-[12px]"
          >
            <option value="build">Build</option>
            <option value="investigation">Investigation (read-only, no code)</option>
          </select>
        </label>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={runValidate}
            disabled={posting || !authorId}
            className="rounded-[8px] border-none bg-[var(--teal)] px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-[0.04em] text-white disabled:opacity-60"
          >
            {posting ? "Validating…" : !authorId ? "Sign in to save" : "Validate"}
          </button>
          <button
            type="button"
            onClick={copyToBacklog}
            className="rounded-[8px] border-[1.5px] border-[var(--teal)] bg-transparent px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-[0.04em] text-[#054750]"
          >
            {copied ? "Copied!" : "Copy text"}
          </button>
        </div>

        {result && (
          <div
            className="flex flex-col gap-1.5 rounded-[8px] border-[1.5px] p-2.5 text-[12px]"
            style={{
              borderColor: !result.passed ? "var(--orange)" : hasSoftWarnings ? "var(--yellow, #D9A441)" : "var(--teal)",
              background: !result.passed
                ? "var(--orange-soft, #FDF3E7)"
                : hasSoftWarnings
                  ? "var(--yellow-soft, #FBF3DD)"
                  : "var(--teal-tint, #EBF6F5)",
              color: "var(--ink-soft)",
            }}
          >
            {result.passed ? (
              <div style={{ color: "#054750", fontWeight: 600 }}>
                {hasSoftWarnings ? (
                  <>⚠️ Spec passed with a soft warning — file references found, blast level determined, no cross-repo conflicts detected, but see below.</>
                ) : (
                  <>✅ Spec looks good — file references found, blast level determined, no cross-repo conflicts detected.</>
                )}{" "}
                Firestore collection dependency detection isn't implemented yet, so still worth a manual look there.
                {result.recommended_tag && <> Tag this post <code>{result.recommended_tag}</code> before filing.</>}

                {/* track:key-outcome-coverage-check (seq #43386) — soft
                    warnings are advisory, never blocking: they're shown
                    here, inside the "passed" branch, not routed to the
                    hard-fail warnings list below. Posting/Save-to-Backlog
                    proceed exactly as on a clean pass. */}
                {hasSoftWarnings && (
                  <div className="mt-2 flex flex-col gap-1" style={{ color: "var(--yellow-dark, #8A6416)" }}>
                    {softWarnings.map((w, i) => (
                      <div key={`${w.code}-${i}`}>⚠ {w.message}</div>
                    ))}
                  </div>
                )}

                {/* Part 4 — context chain indicator: orientation, not navigation. */}
                <div
                  aria-hidden
                  className="mt-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.06em]"
                  style={{ color: "var(--ink-soft)" }}
                >
                  {["Project Background", "PRD", "Spec", "Backlog", "Build"].map((step, i, arr) => {
                    const active = step === "Spec" || (step === "Backlog" && savedBacklogSeq !== null);
                    return (
                      <span key={step} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ color: active ? "#054750" : "var(--ink-soft)", opacity: active ? 1 : 0.5 }}>
                          {step}
                          {step === "Spec" ? " ✓" : ""}
                        </span>
                        {i < arr.length - 1 && <span style={{ opacity: 0.4 }}>→</span>}
                      </span>
                    );
                  })}
                </div>

                {posting && <div style={{ marginTop: 8, fontWeight: 500 }}>Posting spec to Team Log…</div>}
                {postError && (
                  <div style={{ marginTop: 8, fontWeight: 500, color: "var(--orange-dark, #D07A3A)" }}>
                    ⚠ {postError}{" "}
                    <button
                      type="button"
                      onClick={runValidate}
                      style={{ textDecoration: "underline", fontWeight: 700 }}
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Part 1 — validated seq, prominent and copy-able. */}
                {validatedSpecSeq !== null && (
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={copySeq}
                      title="Click to copy"
                      style={{
                        border: "1px solid var(--teal)",
                        borderRadius: 6,
                        padding: "3px 8px",
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#054750",
                        background: "var(--paper,#fff)",
                        cursor: "pointer",
                        userSelect: "text",
                      }}
                    >
                      {seqCopied ? "Copied!" : `Spec #${validatedSpecSeq} validated`}
                    </button>
                  </div>
                )}

                {/* Part 2 — Save to Backlog. */}
                {validatedSpecSeq !== null && savedBacklogSeq === null && !showSaveForm && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={openSaveForm}
                      style={{
                        border: "1px solid var(--teal)",
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontWeight: 600,
                        fontSize: 11,
                        color: "#054750",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      Save to Backlog
                    </button>
                  </div>
                )}

                {savedBacklogSeq !== null && (
                  <div className="mt-2" style={{ fontWeight: 600, color: "#054750" }}>
                    Saved to backlog · #{savedBacklogSeq}
                  </div>
                )}

                {showSaveForm && (
                  <div
                    className="mt-2 flex flex-col gap-1.5 rounded-[8px] border p-2"
                    style={{ borderColor: "var(--line)", background: "var(--paper,#fff)" }}
                  >
                    {/* track:key-outcome-coverage-check (seq #43386) —
                        project is chosen at the top level, above Validate;
                        no second picker here. track:feature-lifecycle-join-
                        keys (seq #45053/#45072) — feature picker moved up
                        there too, for the same reason (see that picker's
                        own comment) — no second one here either. */}
                    {!selectedProjectSlug && (
                      <div style={{ fontSize: 11, color: "var(--ink-soft)" }}>
                        Pick a project to save.
                      </div>
                    )}

                    {saveError && (
                      <div style={{ fontSize: 11, color: "var(--orange-dark, #D07A3A)", fontWeight: 600 }}>
                        ⚠ {saveError}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={confirmSaveToBacklog}
                        disabled={!selectedProjectSlug || saving || !authorId}
                        className="rounded-[6px] border-none bg-[var(--teal)] px-2.5 py-1 text-[11px] font-bold uppercase text-white disabled:opacity-60"
                      >
                        {saving ? "Saving…" : !authorId ? "Sign in to save" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelSaveForm}
                        disabled={saving}
                        className="rounded-[6px] border border-[var(--line)] bg-transparent px-2.5 py-1 text-[11px] font-bold uppercase text-[var(--ink-soft)] disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              result.warnings.map((w, i) => (
                <div key={`${w.code}-${i}`}>
                  <span style={{ color: "var(--orange-dark, #D07A3A)", fontWeight: 700 }}>⚠ {w.code}</span> — {w.message}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {RESIZE_DIRS.map(({ dir, style }) => (
        <div
          key={dir}
          onMouseDown={e => startResize(e, dir)}
          style={{ position: "absolute", zIndex: 4, ...style }}
        />
      ))}
      <ResizeHandle onMouseDown={e => startResize(e, "se")} />
    </div>
  );
}

export default BuildASpecModal;
